import type maplibregl from 'maplibre-gl';
import type { LineLayerSpecification } from 'maplibre-gl';
import { NYC_TRAFFIC_BOUNDS } from '@vitals/shared';

const SOURCE_ID = 'tomtom-traffic';
const CASING_ID = 'tomtom-traffic-casing';
const CORE_ID = 'tomtom-traffic-core';

export function trafficTileUrl(apiKey: string): string {
  return `https://api.tomtom.com/traffic/map/4/tile/flow/relative/{z}/{x}/{y}.pbf?key=${encodeURIComponent(apiKey)}`;
}

export function tomTomCopyrightUrl(apiKey: string): string {
  const url = new URL('https://api.tomtom.com/map/2/copyrights/caption.json');
  url.searchParams.set('key', apiKey);
  return url.toString();
}

type FetchLike = (input: URL, init: { headers: { Accept: string } }) => Promise<Response>;

/** TomTom's terms require API-generated attribution for services that do not
 * render it themselves. A failed/malformed caption prevents displaying tiles. */
export async function fetchTomTomAttribution(
  apiKey: string,
  fetchFn: FetchLike = fetch,
): Promise<string> {
  const response = await fetchFn(new URL(tomTomCopyrightUrl(apiKey)), { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`TomTom copyright request failed (${response.status})`);
  const body = await response.json() as { copyrightsCaption?: unknown };
  if (typeof body.copyrightsCaption !== 'string' || body.copyrightsCaption.trim() === '') {
    throw new Error('TomTom copyright response missing caption');
  }
  return body.copyrightsCaption;
}

export const TRAFFIC_CASING_LAYER: LineLayerSpecification = {
  id: CASING_ID, type: 'line', source: SOURCE_ID, 'source-layer': 'Traffic flow',
  layout: { 'line-cap': 'round', 'line-join': 'round' },
  paint: {
    'line-color': 'rgba(10, 14, 18, 0.65)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 12, 5, 16, 10],
    'line-opacity': 0.7,
  },
};

export const TRAFFIC_CORE_LAYER: LineLayerSpecification = {
  id: CORE_ID, type: 'line', source: SOURCE_ID, 'source-layer': 'Traffic flow',
  layout: { 'line-cap': 'round', 'line-join': 'round' },
  paint: {
    'line-color': ['interpolate', ['linear'], ['get', 'traffic_level'],
      0, '#d73027', 0.45, '#f46d43', 0.7, '#fdae32', 0.85, '#a6d96a', 1, '#38b86b'],
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1, 12, 3, 16, 7],
    'line-opacity': 0.9,
  },
};

const removeTraffic = (map: maplibregl.Map): void => {
  if (map.getLayer(CORE_ID)) map.removeLayer(CORE_ID);
  if (map.getLayer(CASING_ID)) map.removeLayer(CASING_ID);
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
};

export function applyTraffic(
  map: maplibregl.Map,
  options: { enabled: boolean; apiKey?: string; attribution?: string },
): void {
  if (!map.isStyleLoaded()) return;
  if (!options.enabled || !options.apiKey || !options.attribution) {
    removeTraffic(map);
    return;
  }
  if (map.getSource(SOURCE_ID)) return;
  map.addSource(SOURCE_ID, {
    type: 'vector', tiles: [trafficTileUrl(options.apiKey)], minzoom: 0, maxzoom: 22,
    bounds: [...NYC_TRAFFIC_BOUNDS],
    attribution: options.attribution,
  });
  const before = map.getStyle()?.layers?.find((layer) => layer.type === 'symbol')?.id;
  map.addLayer(TRAFFIC_CASING_LAYER, before);
  map.addLayer(TRAFFIC_CORE_LAYER, before);
}
